import { apiBaseOrigin } from '../../../shared/config/api-origin';
import {
  parseRegionalCases,
  parseRegionalDetail,
  parseRegionalOverview,
  parseRegionalRefresh,
  parseRegionalSession,
  parseRegionalWrite,
  type RegionalPort,
  type RegionalSession,
} from '../consumer/regional-monitoring';

export const REGIONAL_API_PREFIX = '/api/risk-warning/regional';
const MAX_RESPONSE_BYTES = 2 * 1024 * 1024;
export class RegionalRequestError extends Error {
  constructor(
    readonly code: string,
    message: string,
    readonly retryable: boolean,
    readonly status = 0,
  ) {
    super(message);
    this.name = 'RegionalRequestError';
  }
}
export function regionalErrorMessage(error: unknown): string {
  return error instanceof RegionalRequestError ? error.message : '暂时无法读取区域数据，请重试。';
}
function messageFor(code: string, status: number): string {
  if (status === 401) return '本地工作会话已过期。再次提交将重新建立会话，记录不会丢失。';
  if (status === 403) return '当前来源或工作会话没有此操作权限。';
  if (code === 'REVISION_CONFLICT') return '该事项已被更新。请重新读取最新记录后再操作。';
  if (code === 'REQUEST_ID_CONFLICT') return '提交标识已绑定另一份内容，请重新读取事项。';
  if (status === 409) return '事项状态已变化，当前操作不能执行。请读取最新记录。';
  if (status === 404) return '未找到对应记录，请刷新列表。';
  if (status === 400) return '提交内容不符合要求，请检查说明、时间和当前状态。';
  if (status === 429) return '请求较频繁，请稍后使用同一提交重试。';
  return '区域服务暂不可用。已保存的记录不会因此自动关闭。';
}
export function createRegionalMonitoringAdapter(
  options: { baseOrigin?: string; fetcher?: typeof fetch; timeoutMs?: number } = {},
): RegionalPort {
  const base = (options.baseOrigin ?? apiBaseOrigin()).replace(/\/$/, '');
  const fetcher = options.fetcher ?? globalThis.fetch.bind(globalThis);
  let session: RegionalSession | null = null;
  let sessionPromise: Promise<RegionalSession> | null = null;
  let sessionEpoch = 0;
  async function request<T>(
    path: string,
    parse: (value: unknown) => T,
    method = 'GET',
    payload?: unknown,
    signal?: AbortSignal,
    authorized = false,
  ): Promise<T> {
    if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
    const controller = new AbortController();
    let timedOut = false;
    const abort = () => controller.abort();
    signal?.addEventListener('abort', abort, { once: true });
    const timer = setTimeout(
      () => {
        timedOut = true;
        controller.abort();
      },
      options.timeoutMs ?? (path === '/refresh' ? 65_000 : 20_000),
    );
    try {
      const headers = new Headers({ Accept: 'application/json' });
      if (method !== 'GET') headers.set('Content-Type', 'application/json');
      if (authorized) {
        if (!session)
          throw new RegionalRequestError(
            'SESSION_REQUIRED',
            messageFor('SESSION_REQUIRED', 401),
            false,
            401,
          );
        headers.set('X-Regional-Session', session.token);
        headers.set('X-Regional-CSRF', session.csrfToken);
      }
      const response = await fetcher(`${base}${REGIONAL_API_PREFIX}${path}`, {
        method,
        headers,
        credentials: 'omit',
        cache: 'no-store',
        signal: controller.signal,
        ...(payload === undefined ? {} : { body: JSON.stringify(payload) }),
      });
      const length = Number(response.headers.get('content-length') ?? 0);
      if (length > MAX_RESPONSE_BYTES) {
        await response.body?.cancel();
        throw new RegionalRequestError(
          'RESPONSE_TOO_LARGE',
          '区域响应超过允许大小，暂未展示。',
          false,
        );
      }
      const reader = response.body?.getReader();
      let text = '',
        bytes = 0;
      if (reader) {
        const decoder = new TextDecoder();
        try {
          for (;;) {
            const chunk = await reader.read();
            if (chunk.done) break;
            bytes += chunk.value.byteLength;
            if (bytes > MAX_RESPONSE_BYTES) {
              await reader.cancel();
              throw new RegionalRequestError(
                'RESPONSE_TOO_LARGE',
                '区域响应超过允许大小，暂未展示。',
                false,
              );
            }
            text += decoder.decode(chunk.value, { stream: true });
          }
          text += decoder.decode();
        } finally {
          reader.releaseLock();
        }
      }
      let body: unknown;
      try {
        body = text ? JSON.parse(text) : {};
      } catch {
        throw new RegionalRequestError(
          'INVALID_RESPONSE',
          '服务返回了无法识别的内容，请稍后重试。',
          !response.ok,
          response.status,
        );
      }
      if (!response.ok) {
        const doc = body && typeof body === 'object' ? (body as Record<string, unknown>) : {};
        const code =
          typeof doc.code === 'string' && /^[A-Z0-9_]{1,80}$/.test(doc.code)
            ? doc.code
            : `HTTP_${response.status}`;
        if (response.status === 401) session = null;
        throw new RegionalRequestError(
          code,
          messageFor(code, response.status),
          doc.retryable === true || response.status >= 500 || response.status === 429,
          response.status,
        );
      }
      try {
        return parse(body);
      } catch {
        throw new RegionalRequestError(
          'INVALID_RESPONSE',
          '区域响应缺少必要字段，暂未将它作为有效数据展示。',
          false,
          response.status,
        );
      }
    } catch (error) {
      if (signal?.aborted) throw new DOMException('Aborted', 'AbortError');
      if (timedOut)
        throw new RegionalRequestError(
          'TIMEOUT',
          '请求超时，提交结果尚未确定。请使用“重试同一提交”核对结果。',
          true,
        );
      if (error instanceof RegionalRequestError) throw error;
      throw new RegionalRequestError(
        'NETWORK',
        '连接中断，提交结果尚未确定。请使用同一提交重试。',
        true,
      );
    } finally {
      clearTimeout(timer);
      signal?.removeEventListener('abort', abort);
    }
  }
  async function ensureSession(): Promise<void> {
    if (session && Date.parse(session.expiresAt) > Date.now() + 5000) return;
    if (!sessionPromise) {
      const epoch = sessionEpoch;
      sessionPromise = request('/session', parseRegionalSession, 'POST', {})
        .then((value) => {
          if (epoch !== sessionEpoch)
            throw new RegionalRequestError(
              'SESSION_REVOKED',
              '工作会话已退出，请重新发起操作。',
              false,
              401,
            );
          session = value;
          return value;
        })
        .finally(() => {
          sessionPromise = null;
        });
    }
    await sessionPromise;
  }
  async function write<T>(path: string, body: unknown, parser: (value: unknown) => T): Promise<T> {
    await ensureSession();
    return request(path, parser, 'POST', body, undefined, true);
  }
  return {
    overview: (signal) => request('/overview', parseRegionalOverview, 'GET', undefined, signal),
    cases: (filter, signal) =>
      request(
        `/cases?status=${encodeURIComponent(filter)}`,
        parseRegionalCases,
        'GET',
        undefined,
        signal,
      ),
    detail: (id, signal) =>
      request(`/cases/${encodeURIComponent(id)}`, parseRegionalDetail, 'GET', undefined, signal),
    refresh: (requestId) => write('/refresh', { requestId }, parseRegionalRefresh),
    create: (command) => write('/cases/manual', command, parseRegionalWrite),
    act: (id, command) =>
      write(`/cases/${encodeURIComponent(id)}/actions`, command, parseRegionalWrite),
    endSession: async () => {
      sessionEpoch++;
      try {
        if (session) await request('/session', () => undefined, 'DELETE', {}, undefined, true);
      } finally {
        session = null;
      }
    },
  };
}
